import React from "react";
import { useData, tail } from "../lib/store.jsx";
import { Kpi, Panel, Combo, Heatmap, DataTable, CalendarHeat } from "../components/ui.jsx";
import { pct1, mlabel, gbp0, REGISTERED_BEDS } from "../lib/data.js";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const FY_MONTHS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];

// Occupancy bands deepen through blue to indigo as they climb; a full house
// is the one pink pop, so it reads instantly.
const OCC_BANDS = [
  { max: 90, color: "rgba(255,255,255,.03)", label: "<90%" },
  { max: 93, color: "rgba(255,255,255,.08)", label: "90–93%" },
  { max: 95, color: "rgba(255,255,255,.10)", label: "93–95%" },
  { max: 97, color: "#e9dfd0", label: "95–97%", dark: true },
  { max: 99, color: "#f6f1e8", label: "97–99%", dark: true },
  { max: 99.999, color: "#dcef8c", label: "99%+", dark: true },
  { max: Infinity, color: "#c8e64a", label: "100%", dark: true },
];

const pc1 = (v) => `${(+v).toFixed(1)}%`;
const n2 = (v) => (+v).toFixed(2);

export default function Occupancy() {
  const { d, m, period } = useData();

  // Monthly average occupancy % with a least-squares trend line.
  const mo = tail(d.occMonthly, period || 24);
  const ys = mo.map((r) => +(+r.occupancy_pct));
  const n = ys.length;
  const sx = ys.reduce((s, _, i) => s + i, 0);
  const sy = ys.reduce((s, y) => s + y, 0);
  const sxx = ys.reduce((s, _, i) => s + i * i, 0);
  const sxy = ys.reduce((s, y, i) => s + i * y, 0);
  const slope = n > 1 ? (n * sxy - sx * sy) / (n * sxx - sx * sx) : 0;
  const intc = n ? (sy - slope * sx) / n : 0;
  const monthly = mo.map((r, i) => ({
    x: mlabel(r.month),
    "Occupancy %": +(+r.occupancy_pct).toFixed(1),
    Trend: +(intc + slope * i).toFixed(1),
  }));

  const occ = m.occFy || [];
  const latestYtd = [...occ].reverse().find((f) => f.ytd);

  // ── Monthly occupancy-rate heatmap (FY × Apr→Mar), brand bands ──────
  const hmRows = m.occMatrix.map((r) => ({
    key: r.label, label: r.label + (r.ytd ? " ·YTD" : ""),
    rowFull: r.label }));
  const hmCols = FY_MONTHS.map((mn) => ({ key: mn, label: MON[mn - 1] }));
  const mxByLabel = {};
  for (const r of m.occMatrix) mxByLabel[r.label] = r;

  // ── Tables ──────────────────────────────────────────────────────────
  const fyCols = [{ key: "metric", label: "Metric" },
    ...occ.map((f) => ({
      key: f.label, label: f.label + (f.ytd ? " YTD" : ""), num: true }))];
  const ytdKeys = occ.filter((f) => f.ytd).map((f) => f.label);

  const cell = (f, fn) => fn(f);
  const annualRows = [
    ["Registered Beds", (f) => f.regBeds],
    ["Avg Daily Occupancy", (f) => n2(f.avgDailyOcc)],
    ["Avg Occupancy Rate", (f) => pc1(f.avgOccRate)],
    ["Peak Monthly Avg", (f) => n2(f.peakMonthlyAvg)],
    ["Trough Monthly Avg", (f) => n2(f.troughMonthlyAvg)],
    ["Monthly Range", (f) => n2(f.monthlyRange)],
    ["Std Deviation", (f) => n2(f.stdDev)],
  ].map(([metric, fn]) => {
    const row = { metric };
    for (const f of occ) row[f.label] = cell(f, fn);
    return row;
  });

  const fullRows = [
    ["Days at 100% Occupancy", (f) => f.daysAtFull],
    ["Total Days Tracked", (f) => f.daysTracked],
    ["100% Occupancy Rate", (f) => pc1(f.fullRatePct)],
    ["Avg Empty Beds / Day", (f) => n2(f.avgEmptyBeds)],
  ].map(([metric, fn]) => {
    const row = { metric };
    for (const f of occ) row[f.label] = cell(f, fn);
    return row;
  });

  const monCols = [{ key: "year", label: "Year" },
    ...FY_MONTHS.map((mn) => ({ key: String(mn), label: MON[mn - 1],
      num: true })),
    { key: "full", label: "Full Year", num: true },
    { key: "best", label: "Best", num: true }];
  const monRows = m.occMatrix.map((r) => {
    const row = { year: r.label + (r.ytd ? " YTD" : ""),
      full: pc1(r.full), best: pc1(r.best) };
    for (const mn of FY_MONTHS) {
      row[String(mn)] = r.cells[mn] == null ? "" : pc1(r.cells[mn]);
    }
    return row;
  });

  const residents = [...d.residents]
    .sort((a, b) => (+a.room || 0) - (+b.room || 0))
    .map((r) => {
      const doa = r.date_of_admission
        ? Date.parse(r.date_of_admission) : null;
      return {
        room: r.room, fund: r.funding_type || "—",
        fee: +r.weekly_fee || 0,
        days: doa ? Math.floor((Date.now() - doa) / 864e5) : null,
      };
    });

  return (
    <>
        <Kpi label="Occupied Beds" color="#c8e64a" value={m.curBeds}
          fmt={(x) => Math.round(x)} meta={`of ${REGISTERED_BEDS}`} />
        <Kpi label="Occupancy %" color="#a9c6a0" value={m.occupancyPct} fmt={pct1}
          meta="current" />
        <Kpi label="YTD Occupancy" color="#f3ece2" value={m.occYtdPct} fmt={pct1}
          meta="financial year to date" />
        <Kpi label="Days at 100%" color="#ec9a74" value={m.daysAtFull}
          fmt={(x) => Math.round(x)}
          meta={latestYtd ? `current FY ${latestYtd.label}` : "this FY"} />
        <Panel className="s12" title="Every day, the last twelve months"
          sub="beds occupied each night, out of 28">
          <CalendarHeat daily={d.daily} />
        </Panel>
        <Panel className="s8" title="Occupancy heatmap"
          sub="average monthly occupancy by financial year — Apr → Mar">
          <Heatmap rows={hmRows} cols={hmCols}
            value={(fyKey, mn) => mxByLabel[fyKey]?.cells[mn] ?? null}
            bands={OCC_BANDS} cellFmt={(v) => `${v.toFixed(1)}%`}
            unit="" showCellValue vClass="hm-v hm-v-lg" />
          <div className="legend">
            {OCC_BANDS.map((b) => (
              <span key={b.label}>
                <i style={{ background: b.color }} />{b.label}
              </span>
            ))}
          </div>
        </Panel>
        <Panel className="s12" title="Monthly occupancy rates"
          sub="average occupancy each month, with full-year and best">
          <DataTable columns={monCols} rows={monRows} />
        </Panel>
        <Panel className="s6" title="Annual overview"
          sub="occupancy stability by financial year">
          <DataTable columns={fyCols} rows={annualRows}
            ytdKeys={ytdKeys} />
        </Panel>
        <Panel className="s6" title="100% occupancy analysis"
          sub="days the home ran completely full">
          <DataTable columns={fyCols} rows={fullRows}
            ytdKeys={ytdKeys} />
        </Panel>
        <Panel className="s12" title="Monthly occupancy"
          sub="average occupancy each month, with trend">
          <Combo data={monthly} x="x"
            bar={{ key: "Occupancy %", name: "Occupancy %" }}
            line={[{ key: "Trend", name: "Trend", dashed: true }]} h={300} trimX
            yDomain={[80, 100]} />
        </Panel>
        <Panel className="s4" title="Resident roll" sub="current residents by room">
          <div className="scroll-y">
            <table>
              <thead><tr>
                <th>Room</th><th>Funding</th>
                <th className="num">Fee /wk</th>
                <th className="num">Days resident</th>
              </tr></thead>
              <tbody>
                {residents.map((r) => (
                  <tr key={r.room}>
                    <td>{r.room}</td>
                    <td>{r.fund}</td>
                    <td className="num">{gbp0(r.fee)}</td>
                    <td className="num">{r.days == null
                      ? "—" : r.days.toLocaleString("en-GB")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
    </>
  );
}
